import { Check, ChevronDown, User, UserCircle } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { UserPersona } from '@/types/persona';
import { usePersona } from '@/contexts/PersonaContext';

const personaConfig = {
  personal: {
    label: 'Personal View',
    description: 'Your timesheet management',
    color: 'bg-green-500 hover:bg-green-600',
    icon: UserCircle,
  },
  manager: {
    label: 'Manager View',
    description: 'Team oversight and approvals',
    color: 'bg-blue-500 hover:bg-blue-600',
    icon: User,
  },
};

interface PersonaBadgeToggleProps {
  className?: string;
}

export function PersonaBadgeToggle({ className }: PersonaBadgeToggleProps) {
  const { currentPersona, setCurrentPersona, availablePersonas } = usePersona();
  const current = personaConfig[currentPersona];
  const CurrentIcon = current.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'inline-flex items-center gap-2 px-3 py-1.5 rounded-full',
            'text-sm font-medium text-white transition-colors',
            'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background',
            current.color,
            className
          )}
          aria-label={`Current view: ${current.label}. Click to switch persona.`}
        >
          <CurrentIcon className="h-4 w-4" />
          <span>{current.label}</span>
          <ChevronDown className="h-4 w-4 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {availablePersonas.map((persona) => {
          const config = personaConfig[persona];
          const Icon = config.icon;
          const isActive = persona === currentPersona;

          return (
            <DropdownMenuItem
              key={persona}
              onClick={() => setCurrentPersona(persona)}
              className={cn(
                'flex items-start gap-3 p-3 cursor-pointer',
                isActive && 'bg-accent'
              )}
            >
              <div
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full',
                  config.color.split(' ')[0],
                  'text-white flex-shrink-0'
                )}
              >
                <Icon className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{config.label}</span>
                  {isActive && (
                    <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {config.description}
                </p>
              </div>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Alternative: Simple Badge variant without dropdown (for reference)
export function PersonaBadgeSimple({ className }: { className?: string }) {
  const { currentPersona } = usePersona();
  const current = personaConfig[currentPersona];
  const CurrentIcon = current.icon;

  return (
    <Badge
      variant="secondary"
      className={cn(
        'inline-flex items-center gap-1.5 px-3 py-1.5',
        current.color,
        'text-white',
        className
      )}
    >
      <CurrentIcon className="h-3.5 w-3.5" />
      <span>{current.label}</span>
    </Badge>
  );
}
